/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { Hidden } from '@coursera/cds-core';
import type { CalendarItem } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';

import { CalendarLoader, WithGetDegreeHomeCalendarQuery } from 'bundles/degree-home/components/v2/Calendar';
import { CalendarDayPicker } from 'bundles/degree-home/components/v2/Calendar/CalendarDayPicker';
import { CalendarFilter } from 'bundles/degree-home/components/v2/Calendar/CalendarFilter';
import { CalendarItemsList } from 'bundles/degree-home/components/v2/Calendar/CalendarItemsList';
import { getCalendarItemsMap } from 'bundles/degree-home/components/v2/Calendar/CalendarUtils';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

const styles = {
  card: (theme: Theme) => css`
    overflow: hidden;
    padding: ${theme.spacing(8)} 0 ${theme.spacing(32)} 0;
  `,
};

type CardProps = {
  degreeId: string;
};

type ContentProps = {
  loading: boolean;
  data: { GetDegreeHomeCalendar: { calendarItems: CalendarItem[] } };
  error: Error | undefined | null;
  refetch: () => void;
};

export const CalendarCardContent: React.FunctionComponent<ContentProps> = ({ loading, data, error, refetch }) => {
  const { setRefetchCalendarItems, setCalendarItemsData, setCalendarItemsLoading } = useDegreeContext();
  useEffect(() => {
    if (data?.GetDegreeHomeCalendar?.calendarItems) {
      setCalendarItemsData(getCalendarItemsMap(data.GetDegreeHomeCalendar.calendarItems));
    }
    setRefetchCalendarItems(() => refetch);
  }, [data, refetch, setCalendarItemsData, setRefetchCalendarItems]);

  useEffect(() => {
    setCalendarItemsLoading(loading);
    // We only want to call this effect when the loading changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!data && loading) {
    return <CalendarLoader />;
  }

  if (error) {
    return null;
  }

  return (
    <Card css={styles.card}>
      <CalendarDayPicker />
      <CalendarFilter />
      <CalendarItemsList />
    </Card>
  );
};

export const CalendarCard: React.FunctionComponent<CardProps> = ({ degreeId }) => {
  const { setDegreeId } = useDegreeContext();
  useEffect(() => {
    setDegreeId(degreeId);
  }, [degreeId, setDegreeId]);

  return useMemo(() => {
    return (
      <Hidden smDown>
        <WithGetDegreeHomeCalendarQuery userId={user.get().id} degreeId={degreeId}>
          {({ loading, data, error, refetch }) => {
            return <CalendarCardContent loading={loading} data={data} error={error} refetch={refetch} />;
          }}
        </WithGetDegreeHomeCalendarQuery>
      </Hidden>
    );
  }, [degreeId]);
};
